import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { createContext, useContext, ReactNode } from 'react';
import { AnimatePresence } from 'motion/react';
import getRouteKey from '@/utils/getRouteKey';
import { createPageTransitionGate } from './pageTransitionGate';

interface PageLoadingContextType {
  isLoading: boolean;
  previewLoading: boolean;
  setPreviewLoading: (loading: boolean) => void;
  onPageExitComplete: () => void;
}

const PageLoadingContext = createContext<PageLoadingContextType | undefined>(undefined);

interface PageLoadingProviderProps {
  children: ReactNode;
}

export const PageLoadingProvider = ({ children }: PageLoadingProviderProps) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(router.asPath.includes('preview=true'));
  const gate = useMemo(() => createPageTransitionGate(setIsLoading), []);

  const handleStart = useCallback((url: string, { shallow }: { shallow: boolean }) => {
    if (shallow) return;
    const currentKey = getRouteKey(router.asPath);
    if (url === router.asPath) {
      gate.retarget(currentKey);
      return;
    }
    const nextKey = getRouteKey(url);
    gate.start(nextKey, nextKey !== currentKey);
  }, [router, gate]);

  const handleComplete = (url: string) => gate.routeComplete(getRouteKey(url));

  // A cancelled route means a newer navigation already took over the loader
  const handleError = (err: { cancelled?: boolean }) => {
    if (err?.cancelled) return;
    gate.fail();
  };

  useEffect(() => {

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleError);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleError);
    };
  }, [router]);

  return (
    <PageLoadingContext.Provider value={{ isLoading, previewLoading, setPreviewLoading, onPageExitComplete: gate.exitComplete }}>
      {children}
    </PageLoadingContext.Provider>
  );
};

export const usePageLoading = (): PageLoadingContextType => {
  const context = useContext(PageLoadingContext);
  return context || {
    isLoading: false,
    previewLoading: false,
    setPreviewLoading: () => {},
    onPageExitComplete: () => {},
  };
};

// Page-level presence: reports when the outgoing page has fully exited so the loader can release
export const PagePresence = ({ children }: { children: ReactNode }) => {
  const { onPageExitComplete } = usePageLoading();
  return (
    <AnimatePresence mode="wait" onExitComplete={onPageExitComplete}>
      {children}
    </AnimatePresence>
  );
};
